import { useId, useState } from 'react';
import { Save } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SALARY_PAYMENT_MODE_OPTIONS } from '@/features/institute/staff/constants';
import { instituteToday } from '@/features/teacher/utils';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatMonth } from '@/lib/format';
import type { SalaryPaymentMonth } from '@/types/payroll';
import type { SalaryPaymentMode } from '@/types/staff';
import { payrollApi } from '../api/payroll.api';
import { salaryPaymentFormSchema, type SalaryPaymentFormValues } from '../schemas/payroll.schema';

export interface RecordSalaryPaymentModalProps {
  staffUuid: string;
  staffName: string;
  /** The month being paid, with what was due — the amount starts at that figure. */
  month: SalaryPaymentMonth;
  /** How they are enrolled as being paid; the mode starts there. */
  defaultMode: SalaryPaymentMode | null;
  onClose: () => void;
  onSaved: () => void;
}

type FieldErrors = Partial<Record<keyof SalaryPaymentFormValues, string>>;

/**
 * Records one month's salary as paid: how much, on what day, how, and the
 * bank's UTR or the cash receipt number.
 *
 * The amount starts at the net pay that was due and stays editable — a month
 * with unpaid leave or an advance recovered pays something else, and the
 * record must say what was actually handed over.
 */
export function RecordSalaryPaymentModal({
  staffUuid,
  staffName,
  month,
  defaultMode,
  onClose,
  onSaved,
}: RecordSalaryPaymentModalProps) {
  const formId = useId();
  const today = instituteToday();
  const [values, setValues] = useState<SalaryPaymentFormValues>({
    amount: month.due ?? '',
    paidOn: today,
    mode: defaultMode ?? ('' as SalaryPaymentMode),
    reference: '',
    note: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function set<K extends keyof SalaryPaymentFormValues>(field: K, value: SalaryPaymentFormValues[K]) {
    setValues((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = salaryPaymentFormSchema.safeParse(values);
    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && !(field in errors)) {
          errors[field as keyof FieldErrors] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);
    try {
      await payrollApi.recordSalaryPayment(staffUuid, { month: month.month, ...parsed.data });
      onSaved();
    } catch (caught) {
      setFormError(
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const monthLabel = formatMonth(`${month.month}-01`);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Record ${monthLabel} salary`}
      description={
        month.due
          ? `${staffName} · ${formatCurrency(month.due)} net was due for ${monthLabel}.`
          : `${staffName} · no salary was set for ${monthLabel}, so enter what was paid.`
      }
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" form={formId} isLoading={isSaving} leftIcon={<Save className="h-4 w-4" />}>
            Record payment
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not record this payment">
            {formError}
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Amount paid (₹)"
            required
            inputMode="decimal"
            autoComplete="off"
            value={values.amount}
            error={fieldErrors.amount}
            onChange={(event) => set('amount', event.target.value)}
          />
          <DatePicker
            label="Paid on"
            required
            max={today}
            value={values.paidOn}
            error={fieldErrors.paidOn}
            onChange={(event) => set('paidOn', event.target.value)}
          />
          <Select
            label="Paid by"
            required
            options={SALARY_PAYMENT_MODE_OPTIONS}
            placeholder="Choose how"
            value={values.mode}
            error={fieldErrors.mode}
            onChange={(event) => set('mode', event.target.value as SalaryPaymentMode)}
          />
          <Input
            label={values.mode === 'BANK_TRANSFER' ? 'UTR / transaction ref.' : 'Receipt number'}
            placeholder="Optional"
            autoComplete="off"
            maxLength={60}
            value={values.reference}
            error={fieldErrors.reference}
            onChange={(event) => set('reference', event.target.value)}
          />
        </div>

        <Input
          label="Note"
          placeholder="Optional — e.g. 2 days unpaid leave deducted"
          maxLength={255}
          value={values.note}
          error={fieldErrors.note}
          onChange={(event) => set('note', event.target.value)}
        />
      </form>
    </Modal>
  );
}
